import React, { useState } from 'react';
import { FaBullhorn, FaLock, FaRegFileAlt, FaCog, FaWhatsapp, FaTimes } from 'react-icons/fa';
import styles from './CampaignDetails.module.css';
import StepHeader from './shared/StepHeader';

const CAMPAIGN_TYPES = ['Marketing', 'Utility', 'Authentication', 'Other'];

// Title and description are read-only by design: there is currently no
// backend endpoint to save just a campaign name/description in isolation,
// so they show the values the campaign was created with instead of a
// fake-editable field that silently discards every keystroke. Tags and
// the internal reference below them are genuinely local-only for now (no
// endpoint persists them yet) — they live in the wizard's session state
// via setData, same as an unsaved draft, not written to disk anywhere.
const CampaignDetails = ({ data, setData }) => {
    const [bannerVisible, setBannerVisible] = useState(true);
    const [tagDraft, setTagDraft] = useState('');

    const tags = data?.tags || [];
    const internalId = data?.internalCampaignId || '';
    const campaignType = data?.campaignType || CAMPAIGN_TYPES[0];

    const addTag = () => {
        const value = tagDraft.trim();
        if (!value || tags.includes(value)) {
            setTagDraft('');
            return;
        }
        setData?.({ ...data, tags: [...tags, value] });
        setTagDraft('');
    };

    const removeTag = (tag) => {
        setData?.({ ...data, tags: tags.filter((t) => t !== tag) });
    };

    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag();
        }
    };

    return (
        <div className={styles.container}>
            <StepHeader
                eyebrow="Step 1 of 5"
                title="Campaign Details"
                subtitle="Give your campaign a name and add a few details to get started."
            />

            {bannerVisible && (
                <div className={styles.banner}>
                    <div className={styles.bannerIcon}>
                        <FaBullhorn />
                    </div>
                    <div className={styles.bannerText}>
                        <div className={styles.bannerTitle}>Let's build an impactful campaign</div>
                        <div className={styles.bannerSubtitle}>
                            A well-named campaign helps you stay organized and measure results effectively.
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.bannerClose}
                        onClick={() => setBannerVisible(false)}
                        aria-label="Dismiss"
                    >
                        <FaTimes />
                    </button>
                </div>
            )}

            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <span className={styles.cardHeaderIcon}><FaRegFileAlt /></span>
                    Basic Information
                </div>

                <div className={styles.field}>
                    <label className={styles.label}>Title</label>
                    <div className={styles.readonlyInput}>
                        {data?.campaignName || 'Untitled Campaign'}
                    </div>
                    <div className={styles.hint}>
                        <FaLock className={styles.hintIcon} />
                        Set when this campaign was created
                    </div>
                </div>

                <div className={styles.fieldRow}>
                    <div className={styles.field}>
                        <label className={styles.label}>Campaign Type</label>
                        <select
                            className={styles.select}
                            value={campaignType}
                            onChange={(e) => setData?.({ ...data, campaignType: e.target.value })}
                        >
                            {CAMPAIGN_TYPES.map((type) => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.field}>
                        <label className={styles.label}>Delivery Channel</label>
                        <div className={styles.channelPill}>
                            <FaWhatsapp className={styles.channelIcon} />
                            WhatsApp
                        </div>
                    </div>
                </div>

                {data?.campaignDescription && (
                    <div className={styles.field}>
                        <label className={styles.label}>Campaign Description</label>
                        <div className={styles.readonlyTextarea}>
                            {data.campaignDescription}
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <span className={styles.cardHeaderIcon}><FaCog /></span>
                    Additional Settings
                </div>

                <div className={styles.fieldRow}>
                    <div className={styles.field}>
                        <label className={styles.label}>Campaign Tags <span className={styles.optional}>(Optional)</span></label>
                        <div className={styles.tagInputWrap}>
                            {tags.map((tag) => (
                                <span key={tag} className={styles.tagChip}>
                                    {tag}
                                    <button type="button" onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`}>
                                        <FaTimes />
                                    </button>
                                </span>
                            ))}
                            <input
                                className={styles.tagInput}
                                type="text"
                                placeholder={tags.length === 0 ? 'Add tags e.g. festival, offers, loyalty' : 'Add another...'}
                                value={tagDraft}
                                onChange={(e) => setTagDraft(e.target.value)}
                                onKeyDown={handleTagKeyDown}
                                onBlur={addTag}
                            />
                        </div>
                    </div>
                    <div className={styles.field}>
                        <label className={styles.label}>Internal Campaign ID <span className={styles.optional}>(Optional)</span></label>
                        <input
                            className={styles.input}
                            type="text"
                            placeholder="e.g. RKSH-2026-001"
                            value={internalId}
                            onChange={(e) => setData?.({ ...data, internalCampaignId: e.target.value })}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CampaignDetails;
